import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface OfferTemplateIframeProps {
  html: string;
  className?: string;
}

/**
 * Renders offer letter HTML inside a sandboxed iframe.
 * Scripts execute naturally. Height auto-adjusts via postMessage.
 */
export function OfferTemplateIframe({ html, className }: OfferTemplateIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(0);

  const htmlWithResize = useMemo(() => injectResizeScript(html), [html]);

  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.source !== iframeRef.current?.contentWindow) return;
    if (event.data?.type === "offer-template-height" && typeof event.data.height === "number") {
      setHeight(event.data.height);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={htmlWithResize}
      // allow-scripts: the resize script + any template scripts run.
      // allow-popups (+ escape-sandbox): the offer letter's external links open
      // with target="_blank" (e.g. the culture deck CTA); without these the new
      // tab is silently blocked. Top-frame navigation is intentionally NOT granted
      // (links are target="_blank"), and allow-same-origin is omitted so the iframe
      // stays an opaque origin — both keep the sandbox as tight as the feature allows.
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      className={className}
      style={{
        width: "100%",
        height: `${height}px`,
        border: "none",
        display: "block",
      }}
      title="Offer letter preview"
    />
  );
}

function injectResizeScript(html: string): string {
  const script = `<script>
(function(){
  function send(){parent.postMessage({type:'offer-template-height',height:document.documentElement.scrollHeight},'*');}
  if(typeof ResizeObserver!=='undefined'){new ResizeObserver(send).observe(document.body);}
  else{window.addEventListener('load',send);window.addEventListener('resize',send);}
  send();
})();
</script>`;

  if (html.includes("</body>")) {
    return html.replace("</body>", `${script}</body>`);
  }
  return html + script;
}
